"use client";

import { Monitor, Smartphone } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useWidgetStore } from "../stores/widget-store";
import type { WidgetConfig } from "../types/widget";
import { PositionPicker } from "./position-picker";
import { Slider } from "@/components/ui/slider";

export function PositionControls() {
  const position = useWidgetStore((state) => state.config.position);
  const margins = useWidgetStore((state) => state.config.margins);
  const setPosition = useWidgetStore((state) => state.setPosition);
  const setMargin = useWidgetStore((state) => state.setMargin);
  const activeDevice = useWidgetStore((state) => state.activeDevice);
  const setActiveDevice = useWidgetStore((state) => state.setActiveDevice);

  const devices = [
    {
      key: "desktop" as keyof WidgetConfig["position"],
      label: "Desktop",
      icon: Monitor,
    },
    {
      key: "mobile" as keyof WidgetConfig["position"],
      label: "Mobile",
      icon: Smartphone,
    },
  ];

  return (
    <div className="cy:space-y-6">
      <Tabs
        defaultValue="desktop"
        className="cy:w-full"
        value={activeDevice}
        onValueChange={(value) => {
          const device = value as keyof WidgetConfig["position"];
          setActiveDevice(device);
        }}
      >
        <TabsList className="cy:w-full cy:flex cy:justify-start  cy:h-[48px] cy:bg-white cy:border-b cy:rounded-none cy:py-0">
          {devices.map((device) => (
            <TabsTrigger
              key={device.key}
              value={device.key}
              className="cy:flex-none cy:border-0 cy:text-sm! cy:border-b-3 cy:-mb-1 cy:border-transparent cy:data-[state=active]:border-b-3 cy:data-[state=active]:border-primary cy:rounded-none cy:shadow-none cy:data-[state=active]:shadow-none cy:p-0 cy:px-5 cy:data-[state=active]:text-primary"
            >
              <device.icon className="cy:h-4 cy:w-4" />
              <span>{device.label}</span>
            </TabsTrigger>
            
          ))}
        </TabsList>

        {devices.map((device) => (
          <TabsContent
            key={device.key}
            value={device.key}
            className="cy:space-y-6 cy:flex cy:py-4"
          >
            {/* Position Controls */}
            <div className="cy:space-y-4">
              <PositionPicker
                value={position[device.key]}
                onChange={(value: string) => setPosition(device.key, value)}
                device={device.key}
              />
            </div>

            {/* Margin Controls */}
            <div className="cy:space-y-4 cy:ml-12">
              <div className="cy:text-base">
                Margin(px)
              </div>
              {[
                { key: "vertical", label: "Vertical" },
                { key: "horizontal", label: "Horizontal" },
              ].map((item) => (
                <div key={item.key} className="cy:flex cy:items-end cy:mb-8 cy:w-[330px]">
                  <div className="cy:flex-1">
                    <div className="cy:text-sm">
                      {item.label}{" "}
                      <span className="cy:text-gray-400 cy:text-sm">
                        (Max 80px)
                      </span>
                    </div>
                    <Slider
                      value={[
                        item.key === "vertical"
                          ? margins[device.key].top
                          : margins[device.key].left
                      ]}
                      max={80}
                      step={1}
                      className="cy:mt-2 cy:w-full cy:data-[orientation=horizontal]:h-3"
                      onValueChange={(value) => {
                        if (item.key === "vertical") {
                          setMargin(device.key, "top", value[0]);
                          setMargin(device.key, "bottom", value[0]);
                        } else {
                          setMargin(device.key, "left", value[0]);
                          setMargin(device.key, "right", value[0]);
                        }
                      }}
                    />  
                  </div>
                  <input
                    type="number"
                    value={item.key === "vertical" ? margins[device.key].top : margins[device.key].left}
                    onChange={(e) => {
                      const value = Math.min(Math.max(Number(e.target.value), 0), 80);
                      if (item.key === "vertical") {
                        setMargin(device.key, "top", value);
                        setMargin(device.key, "bottom", value);
                      } else {
                        setMargin(device.key, "left", value);
                        setMargin(device.key, "right", value);
                      }
                    }}
                    min="0"
                    max="80"
                    className="cy:ml-4! cy:px-4 cy:py-2 cy:bg-[#F4F5FA]! cy:border-input! cy:rounded cy:text-center cy:w-[58px] cy:h-[36px]"
                  />
                  <span className="cy:ml-1">px</span>
                </div>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
